import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Heart, MessageSquare, MessageCircle, CornerDownRight, Users, UserPlus, UserCheck, UserX } from "lucide-react";
import { api } from "@/lib/api";
import { colors } from "@/lib/theme";
import { commentTime } from "@/lib/date";
import { Sheet, Avatar } from "@/components/peeps/ui";

const ICON = {
  like: { Icon: Heart, verb: "liked your comment" },
  reply: { Icon: CornerDownRight, verb: "replied to you" },
  comment: { Icon: MessageSquare, verb: "commented on your post" },
  message: { Icon: MessageCircle, verb: "sent you a message" },
  group_request: { Icon: Users, verb: "requested a new group" },
  group_request_approved: { Icon: UserCheck, verb: "approved your group" },
  group_request_declined: { Icon: UserX, verb: "declined your group request" },
  friend_request: { Icon: UserPlus, verb: "sent you a friend request" },
  friend_accept: { Icon: UserCheck, verb: "accepted your friend request" },
  free_nudge: { Icon: Users, verb: "" },
};

export function NotificationsBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [msgUnread, setMsgUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.getNotifications();
      setItems(res.items || []);
      setUnread(res.unread || 0);
    } catch {}
    try {
      const m = await api.messagesUnread();
      setMsgUnread(m.count || 0);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const openInbox = async () => {
    setOpen(true);
    if (unread > 0) {
      setUnread(0);
      try { await api.readAllNotifications(); } catch {}
    }
  };

  const go = (n) => {
    setOpen(false);
    if (n.type === "message" || n.parent_type === "message") navigate(`/messages/${n.actor_id || n.parent_id}`);
    else if (n.type === "group_request" || n.parent_type === "group_request") navigate(`/admin/group-requests`);
    else if (n.parent_type === "event") navigate(`/event/${n.parent_id}`);
    else if (n.parent_type === "feedback") navigate(`/feedback`);
    else if (n.parent_type === "group") navigate(`/group/${n.parent_id}`);
    else if (n.parent_type === "user") navigate(`/user/${n.parent_id}`);
  };

  return (
    <>
      <button
        data-testid="messages-button"
        onClick={() => navigate("/messages")}
        className="fixed top-4 right-[68px] z-40 w-11 h-11 rounded-full flex items-center justify-center backdrop-blur-xl"
        style={{ background: "rgba(32,33,44,0.85)", border: `1px solid ${colors.border}`, boxShadow: "0 4px 16px rgba(0,0,0,0.3)" }}
      >
        <MessageCircle size={19} color={msgUnread > 0 ? colors.brandSecondary : colors.onSurfaceTertiary} />
        {msgUnread > 0 && (
          <span data-testid="messages-unread-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center font-display font-bold text-[10px]" style={{ background: colors.brand, color: colors.onBrand }}>
            {msgUnread > 9 ? "9+" : msgUnread}
          </span>
        )}
      </button>
      <button
        data-testid="notifications-bell"
        onClick={openInbox}
        className="fixed top-4 right-4 z-40 w-11 h-11 rounded-full flex items-center justify-center backdrop-blur-xl"
        style={{ background: "rgba(32,33,44,0.85)", border: `1px solid ${colors.border}`, boxShadow: "0 4px 16px rgba(0,0,0,0.3)" }}
      >
        <Bell size={19} color={unread > 0 ? colors.brandSecondary : colors.onSurfaceTertiary} />
        {unread > 0 && (
          <span data-testid="notifications-unread-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center font-display font-bold text-[10px]" style={{ background: colors.brand, color: colors.onBrand }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      <Sheet visible={open} onClose={() => setOpen(false)} title="Notifications" testID="notifications-sheet">
        {items.length === 0 ? (
          <div className="text-center py-10">
            <Bell size={26} color={colors.muted} className="mx-auto mb-2" />
            <p className="font-display text-[14px]" style={{ color: colors.onSurfaceTertiary }}>Nothing yet</p>
            <p className="font-display text-[12.5px] mt-1" style={{ color: colors.faint }}>Replies, comments and likes will show up here.</p>
          </div>
        ) : (
          <div className="flex flex-col" data-testid="notifications-list">
            {items.map((n) => {
              const meta = ICON[n.type] || ICON.comment;
              const MetaIcon = meta.Icon;
              return (
                <button
                  key={n.id}
                  data-testid={`notification-${n.id}`}
                  onClick={() => go(n)}
                  className="flex items-start gap-3 py-3 text-left"
                  style={{ borderBottom: `1px solid ${colors.border}`, opacity: n.read ? 0.6 : 1 }}
                >
                  <div className="relative">
                    <Avatar name={n.actor_name} uri={n.actor_photo} size={38} />
                    <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
                      <MetaIcon size={11} color={colors.brandSecondary} fill={n.type === "like" ? colors.brandSecondary : "none"} />
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    {n.type === "free_nudge" ? (
                      <p className="font-display text-[13.5px] leading-snug" style={{ color: colors.onSurface }}>{n.preview}</p>
                    ) : (
                      <>
                        <p className="font-display text-[13.5px] leading-snug" style={{ color: colors.onSurface }}>
                          <span className="font-bold">{n.actor_name}</span> <span style={{ color: colors.onSurfaceTertiary }}>{meta.verb}</span>
                        </p>
                        {n.preview && <p className="font-display text-[12.5px] truncate mt-0.5" style={{ color: colors.faint }}>“{n.preview}”</p>}
                      </>
                    )}
                    <span className="font-mono2 text-[10px]" style={{ color: colors.faint }}>{commentTime(n.created_at)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </Sheet>
    </>
  );
}
